import React, { useState } from "react";
import { AlertTriangle, CalendarClock, CheckCircle, ChevronDown, ChevronUp, Mail, Pencil, RotateCcw, Trash2 } from "lucide-react";
import HouseholdContactDetails from "@/components/HouseholdContactDetails";
import { householdAddressText } from "@/lib/sharing-tree-household-filters";

const PILL_PRIMARY = { background: "rgba(5,47,102,0.08)", color: "var(--fb-primary)", border: "1px solid rgba(5,47,102,0.18)" };
const PILL_MUTED = { background: "var(--fb-bg)", color: "var(--fb-text-muted)", border: "1px solid var(--fb-border)" };
const LONG_INFO = 160;

function sortMembersForDisplay(members = []) {
  const ageForSort = (m) => {
    if (m.age === undefined || m.age === null || m.age === "") return Number.NEGATIVE_INFINITY;
    const age = Number(m.age);
    return Number.isFinite(age) ? age : Number.NEGATIVE_INFINITY;
  };
  return [...members].sort((a, b) => {
    const aS = (a.relationship || "").toLowerCase() === "spouse";
    const bS = (b.relationship || "").toLowerCase() === "spouse";
    if (aS !== bS) return aS ? -1 : 1;
    return ageForSort(b) - ageForSort(a);
  });
}

function Label({ children }) {
  return <div className="text-[10px] font-semibold uppercase tracking-wider mb-1 md:hidden" style={{ color: "var(--fb-text-muted)" }}>{children}</div>;
}

/**
 * One household = one DOM representation. Stacked on phones, a summary grid row from md up.
 * All mutation controls are gated by `writable` (canEdit && !isPriorYear) computed by the card.
 */
export default function EnrolledHouseholdRow({
  h, writable, canEditProfile, selectable, selected, onToggleSelected, onEdit,
  expanded, onToggleMembers, pickupSlots, showFamilyItem, busy, confirmPerm,
  editingWindow, savingWindow, resetting, resending, actions, hiddenFields = [],
}) {
  const [infoOpen, setInfoOpen] = useState(false);
  const isDeleted = !!h.deleted_at;
  const members = h.children || [];
  const childCount = h.child_count ?? 0;
  const seniorCount = h.senior_count ?? 0;
  const isDuplicate = h.duplicate_flag && !isDeleted;
  const displayName = hiddenFields.includes("names") ? "Name hidden" : h.client_name || "Name not shown";
  const address = hiddenFields.includes("address") ? "" : householdAddressText(h.client_address);
  const reminderDate = h.reminder_sent_at
    ? new Date(h.reminder_sent_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;
  const info = hiddenFields.length ? "" : h.additional_information || "";
  const longInfo = info.length > LONG_INFO;

  return (
    <div
      id={`household-${h.id}`}
      className="border-t px-1 sm:px-2 py-3 md:grid md:grid-cols-[minmax(0,1.3fr)_minmax(0,1.35fr)_minmax(0,1.2fr)_auto] md:gap-x-4 md:items-start"
      style={{
        borderColor: "var(--fb-border)",
        opacity: isDeleted ? 0.6 : 1,
        background: selected ? "#F0F4FF" : undefined,
      }}
      data-testid={`household-row-${h.id}`}
    >
      {/* Select + identity */}
      <div className="min-w-0 flex items-start gap-3">
        {selectable && (
          <input type="checkbox" checked={selected} onChange={() => onToggleSelected?.(h.id)}
            className="mt-1.5 w-5 h-5 md:w-4 md:h-4 shrink-0 rounded cursor-pointer accent-[#052F66]"
            data-testid={`select-household-${h.id}`} aria-label={`Select ${h.client_name || h.anonymous_id}`} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-bold px-2 py-0.5 rounded-md tracking-tight"
              style={{ background: "rgba(5,47,102,0.07)", color: "var(--fb-primary)" }}
              data-testid={`household-id-${h.id}`}>
              {h.anonymous_id || "No ID"}
            </span>
            {isDeleted && <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--fb-error)" }}>Removed</span>}
            {isDuplicate && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D" }}>
                <AlertTriangle className="w-3 h-3" /> Possible duplicate
              </span>
            )}
          </div>
          <div className={`font-semibold mt-1 break-words ${isDeleted ? "line-through" : ""}`}>{displayName}</div>
          {showFamilyItem && (
            <div className="text-xs mt-1 break-words">
              <span className="font-semibold" style={{ color: "var(--fb-text-muted)" }}>Family item:</span> <span>{hiddenFields.length ? "Hidden" : h.family_item || "—"}</span>
            </div>
          )}
        </div>
      </div>

      {/* Contact */}
      <div className="min-w-0 mt-2 md:mt-0">
        <Label>Contact</Label>
        <HouseholdContactDetails phone={h.client_phone} email={h.client_email} compact testId={`household-contact-${h.id}`} hiddenFields={hiddenFields} />
        {address && (
          <div className="text-xs mt-1" style={{ color: "var(--fb-text-muted)", overflowWrap: "anywhere" }}>
            <span className="font-semibold">Address:</span> {address}
          </div>
        )}
      </div>

      {/* Pickup + reminder */}
      <div className="min-w-0 mt-2 md:mt-0 space-y-1.5">
        <Label>Pickup &amp; reminder</Label>
        {!isDeleted && (h.pickup_window || (writable && pickupSlots.length > 0)) && (
          <div>
            {writable && pickupSlots.length > 0 ? (
              editingWindow ? (
                <span className="inline-flex max-w-full items-center gap-1">
                  <select autoFocus disabled={savingWindow} defaultValue={h.pickup_window || ""}
                    onChange={(e) => actions.savePickupWindow(h, e.target.value)}
                    onBlur={() => actions.setEditingWindow(null)}
                    aria-label={`Pickup window for ${h.anonymous_id || "household"}`}
                    className="max-w-full text-xs rounded border px-1.5 py-1"
                    style={{ borderColor: "var(--fb-primary)", color: "var(--fb-primary)", background: "white" }}
                    data-testid={`pickup-window-select-${h.id}`}>
                    <option value="">— Unassigned —</option>
                    {pickupSlots.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {savingWindow && <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Saving…</span>}
                </span>
              ) : (
                <button type="button" title="Click to change pickup window" onClick={() => actions.setEditingWindow(h.id)}
                  className="inline-flex max-w-full items-center gap-1 text-left text-xs font-medium px-2 py-0.5 rounded-full hover:opacity-75 transition-opacity break-words"
                  style={PILL_PRIMARY} data-testid={`pickup-window-badge-${h.id}`}>
                  <CalendarClock className="w-3 h-3 shrink-0" />
                  {h.pickup_window || "Set pickup window"}
                </button>
              )
            ) : (
              <span className="inline-flex max-w-full items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full break-words" style={PILL_PRIMARY}>
                <CalendarClock className="w-3 h-3 shrink-0" /> {h.pickup_window}
              </span>
            )}
          </div>
        )}
        {!isDeleted && (
          <div className="flex items-center gap-2 flex-wrap">
            {reminderDate ? (
              <>
                <span className="inline-flex max-w-full items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{ background: "var(--fb-success)18", color: "var(--fb-success)" }}>
                  <CheckCircle className="w-3 h-3" /> Reminder sent {reminderDate}
                </span>
                {writable && (
                  <button type="button" onClick={() => actions.resetReminder(h)} disabled={resetting}
                    title="Clear reminder so this household can be re-sent"
                    className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded hover:bg-gray-100"
                    style={{ color: "var(--fb-text-muted)" }}>
                    <RotateCcw className="w-3 h-3" /> {resetting ? "Resetting…" : "Reset"}
                  </button>
                )}
              </>
            ) : (
              <>
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={PILL_MUTED}>Not sent</span>
                {writable && (h.client_email && !hiddenFields.includes("email") ? (
                  <button type="button" onClick={() => actions.resendReminder(h)} disabled={resending}
                    title="Send pickup reminder email to this household now"
                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-medium hover:bg-blue-50"
                    style={{ borderColor: "var(--fb-primary)", color: "var(--fb-primary)" }}>
                    <Mail className="w-3 h-3" /> {resending ? "Sending…" : "Re-send reminder"}
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={PILL_MUTED}>{hiddenFields.includes("email") ? "Email hidden" : "No email on file"}</span>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Members + actions */}
      <div className="mt-2 md:mt-0 flex items-start justify-between md:justify-end gap-3 flex-wrap">
        <div className="md:text-right">
          {members.length > 0 ? (
            <button type="button" onClick={() => onToggleMembers(h.id)}
              className="inline-flex items-center gap-1 text-sm font-medium hover:underline py-1"
              style={{ color: "var(--fb-text-muted)" }}
              data-testid={`toggle-members-${h.id}`} aria-expanded={expanded} aria-controls={`member-list-${h.id}`}
              title={`${expanded ? "Hide" : "Show"} household members`}>
              {members.length} members
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          ) : (
            <div className="text-sm py-1" style={{ color: "var(--fb-text-muted)" }}>0 members</div>
          )}
          {(childCount > 0 || seniorCount > 0) && (
            <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>
              {childCount > 0 && <span>{childCount} child{childCount !== 1 ? "ren" : ""}</span>}
              {childCount > 0 && seniorCount > 0 && " · "}
              {seniorCount > 0 && <span>{seniorCount} senior{seniorCount !== 1 ? "s" : ""}</span>}
            </div>
          )}
        </div>
        {writable && !isDeleted && (
          <div className="flex items-center gap-1">
            {canEditProfile && (
              <button type="button" title="Edit household" aria-label={`Edit household ${h.anonymous_id || ""}`.trim()}
                onClick={() => onEdit(h)} className="p-2 rounded-lg hover:bg-stone-100"
                data-testid={`edit-household-${h.id}`}>
                <Pencil className="w-4 h-4" style={{ color: "var(--fb-primary)" }} />
              </button>
            )}
            <button type="button" title="Remove household" aria-label={`Remove household ${h.anonymous_id || ""}`.trim()}
              onClick={() => actions.softDelete(h)} disabled={!!busy} className="p-2 rounded-lg hover:bg-rose-50 disabled:opacity-50">
              <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
            </button>
          </div>
        )}
      </div>

      {/* Full-width extras */}
      {(info || (expanded && members.length > 0) || (writable && isDeleted)) && (
        <div className="md:col-span-4 mt-2 space-y-2 min-w-0">
          {info && (
            <div className="text-xs whitespace-pre-wrap break-words rounded-lg px-3 py-2"
              style={{ background: "var(--fb-bg)", border: "1px dashed var(--fb-border)" }}
              data-testid={`household-information-${h.id}`}>
              <span className="font-semibold">Additional information (not printed on labels):</span>{" "}
              {longInfo && !infoOpen ? `${info.slice(0, LONG_INFO).trimEnd()}…` : info}
              {longInfo && (
                <button type="button" onClick={() => setInfoOpen((v) => !v)} aria-expanded={infoOpen}
                  className="ml-1 font-semibold underline" style={{ color: "var(--fb-primary)" }}>
                  {infoOpen ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          )}
          {expanded && members.length > 0 && (
            <div id={`member-list-${h.id}`} className="space-y-1" data-testid={`member-list-${h.id}`}>
              {sortMembersForDisplay(members).map((m, i) => (
                <div key={m.id || `${h.id}-member-${i}`} className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs px-3 py-1.5 rounded-lg"
                  style={{ background: "var(--fb-bg)", border: "1px solid var(--fb-border)" }}>
                  {(m.anonymous_child_id || m.member_id || m.anonymous_id) &&
                    <span className="font-mono shrink-0" style={{ color: "var(--fb-text-muted)" }}>{m.anonymous_child_id || m.member_id || m.anonymous_id}</span>}
                  <span className="font-medium min-w-0 flex-1 break-words">{hiddenFields.includes("names") ? "Name hidden" : m.name || "—"}</span>
                  {m.age !== undefined && m.age !== null && <span className="tabular-nums shrink-0" style={{ color: "var(--fb-text-muted)" }}>Age {m.age}</span>}
                  {m.gender && <span className="shrink-0" style={{ color: "var(--fb-text-muted)" }}>{m.gender}</span>}
                  {m.relationship && <span className="shrink-0 font-medium" style={{ color: m.relationship === "Spouse" ? "var(--fb-primary)" : "var(--fb-text-muted)" }}>{m.relationship}</span>}
                </div>
              ))}
            </div>
          )}
          {writable && isDeleted && (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => actions.restore(h)} disabled={!!busy}
                className="text-xs font-semibold px-3 py-1.5 rounded border" style={{ borderColor: "var(--fb-border)", color: "var(--fb-primary)" }}>
                {busy === "restoring" ? "Restoring…" : "Restore"}
              </button>
              {confirmPerm ? (
                <>
                  <span className="text-xs" style={{ color: "var(--fb-error)" }}>Are you sure? This cannot be undone.</span>
                  <button type="button" onClick={() => actions.permanentDelete(h)} disabled={!!busy}
                    className="text-xs font-semibold px-3 py-1.5 rounded text-white" style={{ background: "var(--fb-error)" }}>
                    {busy === "purging" ? "Deleting…" : "Yes, permanently delete"}
                  </button>
                  <button type="button" onClick={() => actions.setConfirmPerm(null)}
                    className="text-xs px-2 py-1.5 rounded border" style={{ borderColor: "var(--fb-border)" }}>Cancel</button>
                </>
              ) : (
                <button type="button" onClick={() => actions.setConfirmPerm(h.id)}
                  className="text-xs font-semibold px-3 py-1.5 rounded border" style={{ borderColor: "var(--fb-error)", color: "var(--fb-error)" }}>
                  Permanently delete
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
